/*
 * Copyright (c) 2025 Cozy Intellect Inc.
 * File Path: packages/sharedlib/src/stores/ui-calendar-events/core.store.ts
 */

import { derived } from 'svelte/store';

import type { AugmentedUniqueEvents, MemoryDay } from '@repo/common';
import type {
  UICalendarEvent,
  UICalendarStoreState,
  UICalendarTransformContext
} from '@repo/common/types/UICalendar';

// Import calendar utility function
import { getCalendarDaysWithEvents } from '@repo/toolslib/src/utils/calendar';

// Import utility functions
import { groupEventsByDate, groupEventsBySection, transformMemoryDaysToUIEvents } from './utils';

// Import existing stores
import { getUnifiedRange } from '../calendar-range.config';
import { calendarEvents } from '../calendar.store';
import { calendarEventStore } from '../calendarEvent.store';
import { calendarViewStore } from '../provider.calendars.store';
import { enabledCalendars } from '../selected.calendars.store';
import { isTranscriptDeleted, transcriptMap, transcripts } from '../transcripts';

// All transformation functions moved to utils.ts

/**
 * Direct computation function for UI events.
 * Note: Debouncing/throttling will be applied at the store level, not function level,
 * to work properly with Svelte's reactive system.
 */
function computeUIEvents(
  memoryDays: MemoryDay[],
  context: UICalendarTransformContext
): UICalendarEvent[] {
  return transformMemoryDaysToUIEvents(memoryDays, context);
}

/**
 * Create MemoryDay objects from raw calendar events and transcript data.
 */
async function createMemoryDaysFromCalendarEvents(
  calendarEvents: any,
  transcriptMapSource: Map<string, any>,
  viewRange: any,
  currentDate: Date,
  calendarEventMap?: Map<string, any>
): Promise<MemoryDay[]> {
  const eventsByDate = calendarEvents?.eventsByDate || {};
  const transcriptItems = [...transcriptMapSource.values()].filter((t: any) => {
    if (isTranscriptDeleted(t)) return false;

    // For transcripts, use the meeting date if available, otherwise fall back to createdAt
    const meetingDate = t.meeting?.date
      ? new Date(t.meeting.date)
      : t.createdAt
        ? new Date(t.createdAt)
        : null;
    if (!meetingDate) return false;

    if (meetingDate < new Date(viewRange.start)) return false;
    if (meetingDate > new Date(viewRange.end)) return false;
    return true;
  });

  // Create transcript map by event ID
  // Note: transcriptItems are TranscriptSummary objects - we'll need to fetch full transcripts later
  const transcriptMap: Record<string, any> = {};
  const unmappedTranscripts: any[] = [];

  transcriptItems.forEach((transcript: any) => {
    if (transcript?.eventId) {
      // Store the summary for now - full transcript will be fetched when needed for UI
      transcriptMap[transcript.eventId] = transcript;
    } else {
      unmappedTranscripts.push(transcript);
    }
  });

  // Create augmented events structure
  const augmentedEventsByDate: Record<string, AugmentedUniqueEvents> = {};

  for (const [eventDate, events] of Object.entries(eventsByDate)) {
    const augmentedEvents: AugmentedUniqueEvents = {} as AugmentedUniqueEvents;
    for (const event of Object.values(events as Record<string, any>)) {
      if (!event.uid) continue;
      const transcript = transcriptMap[event.uid];

      // Enrich the event with stored calendar event data (workspaceId, tags, etc.)
      const enrichedEvent =
        calendarEventStore.getEnrichedCalendarEvent(event.uid, event, calendarEventMap) || event;

      augmentedEvents[event.uid] = { event: enrichedEvent as any, transcript };
    }
    augmentedEventsByDate[eventDate] = augmentedEvents;
  }

  // console.log('🔍 Memory Days Debug - Processing data:', {
  //   viewRange,
  //   eventsByDateKeys: Object.keys(eventsByDate),
  //   augmentedEventsByDateKeys: Object.keys(augmentedEventsByDate),
  //   transcriptItemsCount: transcriptItems.length,
  //   unmappedTranscriptsCount: unmappedTranscripts.length
  // });

  // Generate MemoryDay objects using the calendar utility
  const memoryDays = await getCalendarDaysWithEvents(
    new Date(viewRange.start),
    new Date(viewRange.end),
    augmentedEventsByDate,
    unmappedTranscripts,
    currentDate.getMonth(),
    currentDate
  );

  return memoryDays;
}

// Cache for expensive UI calendar events computation
let uiCalendarEventsCache: {
  lastCalendarEventsHash: string;
  lastEnabledCalendarsHash: string;
  lastTranscriptsHash: string;
  lastCalendarEventMap: Map<string, any> | null;
  lastDateHash: string;
  lastRangeHash: string;
  cachedResult: UICalendarEvent[];
} | null = null;

// Helper to create a simple hash of data for cache invalidation
function createDataHash(data: any): string {
  if (!data.start || !data.end) return JSON.stringify(data);
  return JSON.stringify({
    start: data.start.split('T')[0],
    end: data.end.split('T')[0]
  });
}

// Track active subscriptions for debugging
let activeSubscriptions = 0;

// Internal derived store without subscription tracking
const _uiCalendarEventsInternal = derived(
  [calendarEvents, enabledCalendars, transcripts, transcriptMap, calendarEventStore],
  (
    [$calendarEvents, $enabledCalendars, $transcripts, $transcriptMap, $calendarEventStore],
    set: (value: UICalendarEvent[]) => void
  ) => {
    try {
      // Use snapshots for frequently changing stores
      const currentDate = new Date();

      // Use the same unified range as calendar.store.ts for consistency
      const unifiedRange = getUnifiedRange(currentDate);

      const viewRange = {
        start: unifiedRange.startISO,
        end: unifiedRange.endISO
      };

      // Create hashes for cache invalidation
      const calendarEventsHash = createDataHash($calendarEvents);
      const enabledCalendarsHash = createDataHash($enabledCalendars.map((c) => c.id));
      const transcriptsHash = createDataHash({
        size: $transcriptMap.size,
        // Hash a few recent transcript IDs to detect changes without full serialization
        recentIds: Array.from($transcriptMap.keys()).slice(0, 10),
        // Include workspace IDs to detect workspace changes
        workspaceIds: Array.from($transcriptMap.values()).slice(0, 10).map((t) => t.workspaceId)
      });
      const dateHash = currentDate.toDateString(); // Daily cache invalidation
      const rangeHash = createDataHash(viewRange); // Include range in cache invalidation

      // Check if we can use cached result
      // Only use cache if it has valid data (length > 0) or if the hashes indicate no data should exist
      if (
        uiCalendarEventsCache &&
        uiCalendarEventsCache.cachedResult.length > 0 &&
        uiCalendarEventsCache.lastCalendarEventsHash === calendarEventsHash &&
        uiCalendarEventsCache.lastEnabledCalendarsHash === enabledCalendarsHash &&
        uiCalendarEventsCache.lastTranscriptsHash === transcriptsHash &&
        uiCalendarEventsCache.lastCalendarEventMap === $calendarEventStore.events &&
        uiCalendarEventsCache.lastDateHash === dateHash &&
        uiCalendarEventsCache.lastRangeHash === rangeHash
      ) {
        console.log('🚀 UI Calendar: Using cached result', {
          cachedEvents: uiCalendarEventsCache.cachedResult.length,
          sampleEvents: uiCalendarEventsCache.cachedResult.slice(0, 3).map((e) => ({
            title: e.title,
            start: e.start.toISOString()
          }))
        });
        set(uiCalendarEventsCache.cachedResult);
        return;
      }

      console.log('🔄 UI Calendar: Cache miss, performing full recalculation', {
        hasCache: !!uiCalendarEventsCache,
        cachedEventCount: uiCalendarEventsCache?.cachedResult?.length || 0,
        calendarChanged:
          !uiCalendarEventsCache ||
          uiCalendarEventsCache.lastCalendarEventsHash !== calendarEventsHash,
        calendarsChanged:
          !uiCalendarEventsCache ||
          uiCalendarEventsCache.lastEnabledCalendarsHash !== enabledCalendarsHash,
        transcriptsChanged:
          !uiCalendarEventsCache || uiCalendarEventsCache.lastTranscriptsHash !== transcriptsHash,
        dateChanged: !uiCalendarEventsCache || uiCalendarEventsCache.lastDateHash !== dateHash,
        rangeChanged: !uiCalendarEventsCache || uiCalendarEventsCache.lastRangeHash !== rangeHash,
        viewRange
      });

      // Create MemoryDays from calendar events
      createMemoryDaysFromCalendarEvents(
        $calendarEvents,
        $transcriptMap,
        viewRange,
        currentDate,
        $calendarEventStore.events
      )
        .then((memoryDays) => {
          // Create transformation context
          const context: UICalendarTransformContext = {
            currentDate,
            dateRange: viewRange,
            enabledCalendars: $enabledCalendars.map((c) => c.id),
            transcriptsMap: $transcriptMap,
            transcriptsByEventId: $transcripts.eventMap,
            timeZone: (() => {
              try {
                return Intl.DateTimeFormat().resolvedOptions().timeZone;
              } catch (error) {
                console.warn(
                  'Failed to get timezone from Intl.DateTimeFormat, using UTC as fallback:',
                  error
                );
                return 'UTC';
              }
            })()
          };

          // Apply event transformation
          const uiEvents = computeUIEvents(memoryDays, context);

          console.log('🔍 UI Calendar: Final result', {
            uiEventsCount: uiEvents.length,
            sampleEvents: uiEvents.slice(0, 3).map((e) => ({
              title: e.title,
              start: e.start.toISOString(),
              isUpcoming: e.isUpcoming
            })),
            futureEvents: uiEvents.filter((e) => e.isUpcoming).length
          });

          // Cache the result
          uiCalendarEventsCache = {
            lastCalendarEventsHash: calendarEventsHash,
            lastEnabledCalendarsHash: enabledCalendarsHash,
            lastTranscriptsHash: transcriptsHash,
            lastCalendarEventMap: $calendarEventStore.events,
            lastDateHash: dateHash,
            lastRangeHash: rangeHash,
            cachedResult: uiEvents
          };

          set(uiEvents);
        })
        .catch((error) => {
          console.error('Error creating UI calendar events:', error);
          set([]); // Return empty array on error
        });
    } catch (error) {
      console.error('Error in uiCalendarEvents store:', error);
      set([]); // Return empty array on error
    }
  },
  [] // Initial value
);

/**
 * Core semi-derived store for UI calendar events with subscription tracking.
 * This is the main store with intelligent caching to avoid expensive recalculations.
 */
export const uiCalendarEvents = {
  subscribe: (run: (value: UICalendarEvent[]) => void) => {
    activeSubscriptions++;

    const unsubscribe = _uiCalendarEventsInternal.subscribe(run);

    return () => {
      activeSubscriptions--;

      // Clear cache when no subscribers remain to prevent memory leaks
      if (activeSubscriptions === 0) {
        // Break circular references in cached events to help GC
        if (uiCalendarEventsCache?.cachedResult) {
          for (const event of uiCalendarEventsCache.cachedResult) {
            // Null out the large object references to break circular refs
            (event as any).originalEvent = null;
            (event as any).originalTranscript = null;
          }
        }

        uiCalendarEventsCache = null;
      }

      unsubscribe();
    };
  }
};

// deduplicateMemoryDays function moved to utils.ts

/**
 * Derived store for events grouped by date.
 */
export const uiCalendarEventsByDate = derived([uiCalendarEvents], ([$events]) =>
  groupEventsByDate($events)
);

/**
 * Derived store for events grouped by time sections.
 */
export const uiCalendarEventsBySection = derived([uiCalendarEvents], ([$events]) => {
  const currentDate = new Date(); // Fresh date for section calculations
  return groupEventsBySection($events, currentDate);
});

/**
 * Complete store state combining all UI calendar data.
 */
export const uiCalendarStoreState = derived(
  [
    uiCalendarEvents,
    uiCalendarEventsByDate,
    uiCalendarEventsBySection,
    enabledCalendars,
    calendarViewStore
  ],
  ([
    $events,
    $eventsByDate,
    $eventsBySection,
    $enabledCalendars,
    $viewRange
  ]): UICalendarStoreState => {
    return {
      // Core data
      events: $events,
      eventsByDate: $eventsByDate,
      eventsBySection: $eventsBySection,
      viewEvents: {
        week: [], // Will be computed by view-specific stores
        month: [],
        day: [],
        agenda: [],
        overview: { upcoming: [], recent: [] }
      },

      // Store metadata
      lastUpdated: new Date(),
      dateRange: $viewRange,
      enabledCalendars: $enabledCalendars.map((c) => c.id),

      // Status flags
      isLoading: false,
      error: null,
      hasData: $events.length > 0
    };
  }
);

// Clean up on HMR to prevent memory leaks
if (typeof import.meta.hot !== 'undefined') {
  import.meta.hot?.dispose(() => {
    uiCalendarEventsCache = null;
  });
}
